import {t} from '@lingui/macro';
import React, {FC} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {NumberInputBox} from '../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {CustomFieldAttribute} from '../../../models/api/customFieldAttributeModel';
import {CustomFieldModel, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {ResourceMap} from '../../../store/appTypes';
import {BooleanPopover} from './booleanPopover';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {DateTimePopoverContainer} from './dateTimePopoverContainer';
import {InboxPopoverContainer} from './inboxPopoverContainer';
import {TeammatePopoverContainer} from './teammatePopoverContainer';

/*
 * Props.
 */

interface CreateFormInputCustomAttributeProps {
  customFields: ResourceMap<CustomFieldModel>;
  customAttribute: CustomFieldAttribute;
  isDisabled?: boolean;
  onUpdate: (customFieldId: number, value: string) => void;
  onDelete: HandlerOf<number>;
}

/*
 * Component.
 */

export const CreateFormInputCustomAttribute: FC<CreateFormInputCustomAttributeProps> = (props) => {
  const {customFields, customAttribute, isDisabled, onUpdate, onDelete} = props;
  const customField = customFields[customAttribute.id];

  const localize = useLocalize();

  if (!customField) {
    return null;
  }

  const onUpdateWrapper = (value: string) => {
    onUpdate(customField.id, value);
  };
  const onDeleteWrapper = () => onDelete(customField.id);

  switch (customField.type) {
    case CustomFieldsEnum.STRING:
      return (
        <CreateFormInputDisplay
          key={customAttribute.id}
          title={customField.name}
          input={
            <TextBox
              placeholder={localize(t('create-form-custom-field-text-placeholder')`Add ${customField.name}`)}
              value={customAttribute.value}
              onChange={onUpdateWrapper}
              noMargin
              isDisabled={isDisabled}
            />
          }
        />
      );

    case CustomFieldsEnum.NUMBER:
      return (
        <CreateFormInputDisplay
          key={customAttribute.id}
          title={customField.name}
          input={
            <NumberInputBox
              value={customAttribute.value === '' ? undefined : Number(customAttribute.value)}
              placeholder={localize(t('create-form-custom-field-number-placeholder')`Add value`)}
              onChange={(number) => onUpdateWrapper(number.toString())}
              noMargin
              isDisabled={isDisabled}
            />
          }
        />
      );

    case CustomFieldsEnum.TEAMMATE:
      return (
        <TeammatePopoverContainer
          key={customAttribute.id}
          title={customField.name}
          teammateEmail={customAttribute.value}
          onTeammateSelect={(teammate) => onUpdateWrapper(teammate.email)}
          onDelete={onDeleteWrapper}
          shouldShowDeleteOptionInDropdown
          isDisabled={isDisabled}
        />
      );

    case CustomFieldsEnum.BOOLEAN:
      return (
        <BooleanPopover
          key={customAttribute.id}
          title={customField.name}
          value={customAttribute.value}
          onSelect={onUpdateWrapper}
          onDelete={onDeleteWrapper}
          shouldShowDeleteOptionInDropdown
          isDisabled={isDisabled}
        />
      );

    case CustomFieldsEnum.DATETIME:
      return (
        <DateTimePopoverContainer
          key={customAttribute.id}
          title={customField.name}
          value={customAttribute.value}
          onSelect={onUpdateWrapper}
          onDelete={onDeleteWrapper}
          shouldShowClearInDropdown
          isDisabled={isDisabled}
        />
      );

    case CustomFieldsEnum.INBOX:
      return (
        <InboxPopoverContainer
          key={customAttribute.id}
          title={customField.name}
          inboxPublicId={customAttribute.value}
          onSelect={onUpdateWrapper}
          onDelete={onDeleteWrapper}
          shouldShowDeleteOptionInDropdown
          isDisabled={isDisabled}
        />
      );

    default:
      return null;
  }
};
